import { useCallback } from 'react';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { detectLanguage, isLanguage, isLoaded, registerDictionary, translate, type Language } from './translate';

/**
 * The language the app is shown in: kept in this browser's localStorage under `kindra.language` (a device
 * preference like the theme, so it stays after sign-out). Until the member chooses, it follows the device.
 */
interface LanguageState {
  language: Language;
  setLanguage: (language: Language) => void;
}

function applyLanguage(language: Language): void {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = language;
  }
}

const initial = (): Language =>
  detectLanguage(typeof navigator === 'undefined' ? undefined : navigator.languages ?? [navigator.language]);

/** localStorage that never throws (private mode, blocked storage). */
const safeStorage = createJSONStorage(() => ({
  getItem: (key) => {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  setItem: (key, value) => {
    try {
      localStorage.setItem(key, value);
    } catch {
      // not persisting is acceptable; the choice still applies to this visit
    }
  },
  removeItem: (key) => {
    try {
      localStorage.removeItem(key);
    } catch {
      // ignore
    }
  },
}));

export const useLanguageStore = create<LanguageState>()(
  persist(
    (set) => ({
      language: initial(),
      setLanguage: (language) => {
        if (!isLanguage(language)) return;
        // the words of a language are fetched the first time it is chosen; the screen changes once they are here
        const apply = () => {
          applyLanguage(language);
          set({ language });
        };
        if (isLoaded(language)) apply();
        else void loadLanguage(language).then(apply);
      },
    }),
    {
      name: 'kindra.language',
      storage: safeStorage,
      partialize: (state) => ({ language: state.language }),
      onRehydrateStorage: () => (state) => {
        if (state) applyLanguage(isLanguage(state.language) ? state.language : 'en');
      },
    },
  ),
);

/** The text in the language chosen now, for code that is not a component (helpers that format a label). */
export function t(text: string, vars?: Record<string, string | number | null | undefined>): string {
  return translate(useLanguageStore.getState().language, text, vars);
}

/** The same, under the name used where a component's own `t` (from `useT`) would be shadowed or is not available. */
export const tNow = t;

/** `t` for a component: the screen is drawn again when the language changes. */
export function useT(): typeof t {
  const language = useLanguageStore((state) => state.language);
  return useCallback((text, vars) => translate(language, text, vars), [language]);
}

/** The locale dates and times are written in: Vietnamese when the app is, English (US) otherwise. */
export const dateLocale = (): string => (useLanguageStore.getState().language === 'vi' ? 'vi-VN' : 'en-US');

/** Fetches the words of `language` (nothing for English, and nothing again once they are here). */
export async function loadLanguage(language: Language): Promise<void> {
  if (isLoaded(language)) return;
  try {
    const { vi } = await import('./vi');
    registerDictionary('vi', vi);
  } catch {
    // offline: the app stays in English until the words can be fetched
  }
}
